// ============================================
// Chat Panel Component
// Main chat interface with channels and messages
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from './ChatProvider';
import ChannelList from './ChannelList';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import TypingIndicator from './TypingIndicator';
import ChatSearch from './ChatSearch';
import PresenceIndicator from './PresenceIndicator';
import { ChannelSettingsModal } from './ChannelManagement';
import {
  VideoRoom,
  IncomingCallModal,
  HuddleIndicator,
  StartHuddleButton
} from '../video';
import useVideoCall from '../../hooks/useVideoCall';
import { getAnyValidToken } from '../../utils/authUtils';

/**
 * Main chat panel containing channel list and message area
 */
const ChatPanel = () => {
  const {
    closeChat,
    minimizeChat,
    currentChannel,
    switchChannel,
    connectionState,
    isConnected,
    notificationPermission,
    notificationsEnabled,
    setNotificationsEnabled,
    soundEnabled,
    setSoundEnabled,
    requestNotificationPermission,
    searchOpen,
    setSearchOpen,
    clearSearch,
    myPresenceStatus,
    setPresenceStatus,
    fetchPinnedMessages,
    currentUser
  } = useChat();

  const isAdmin = currentUser?.type === 'admin';

  const [showSettings, setShowSettings] = useState(false);
  const [showPinnedMessages, setShowPinnedMessages] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [loadingPinned, setLoadingPinned] = useState(false);
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);

  // Fetch video enabled setting
  useEffect(() => {
    const fetchVideoSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_URL}/api/admin/settings/video_enabled/public`);
        if (response.ok) {
          const data = await response.json();
          setVideoEnabled(data.setting_value === 'true');
        }
      } catch (err) {
        console.error('Error fetching video setting:', err);
        // Keep default of true if fetch fails
      }
    };
    fetchVideoSetting();
  }, []);

  // Video call state
  const token = getAnyValidToken();
  const {
    isInCall,
    currentRoom,
    videoToken,
    isLoading: videoLoading,
    error: videoError,
    pendingInvitations,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    leaveCall,
    joinHuddle,
    leaveHuddle,
    getHuddleStatus,
    clearError: clearVideoError
  } = useVideoCall(token);

  const [huddleStatus, setHuddleStatus] = useState(null);
  const [loadingHuddle, setLoadingHuddle] = useState(false);

  // Fetch pinned messages when modal opens
  const handleShowPinned = async () => {
    if (!currentChannel) return;
    setShowPinnedMessages(true);
    setLoadingPinned(true);
    try {
      const data = await fetchPinnedMessages(currentChannel.id);
      if (data) {
        setPinnedMessages(data.pinned_messages || []);
      }
    } finally {
      setLoadingPinned(false);
    }
  };

  const handleChannelSelect = (channel) => {
    // Clear search when switching channels
    if (searchOpen) {
      clearSearch();
    }
    switchChannel(channel);
  };

  // Fetch huddle status when channel changes
  useEffect(() => {
    const fetchHuddleStatus = async () => {
      if (currentChannel && !currentChannel.is_dm) {
        setLoadingHuddle(true);
        try {
          const status = await getHuddleStatus(currentChannel.id);
          setHuddleStatus(status);
        } finally {
          setLoadingHuddle(false);
        }
      } else {
        setHuddleStatus(null);
      }
    };
    fetchHuddleStatus();
  }, [currentChannel, getHuddleStatus]);

  // Poll huddle status for active channels (less frequently to reduce load)
  useEffect(() => {
    if (!currentChannel || currentChannel.is_dm || isInCall) return;

    const pollInterval = setInterval(async () => {
      const status = await getHuddleStatus(currentChannel.id);
      setHuddleStatus(status);
    }, 60000); // Poll every 60 seconds

    return () => clearInterval(pollInterval);
  }, [currentChannel, isInCall, getHuddleStatus]);

  // Handle starting a DM video call
  const handleStartDMCall = useCallback(async () => {
    if (!currentChannel?.is_dm || !currentChannel.dm_other_participant) return;

    const otherUser = currentChannel.dm_other_participant;
    // Backend returns 'type' and 'id', not 'user_type' and 'user_id'
    await startCall(otherUser.type, otherUser.id);
  }, [currentChannel, startCall]);

  // Handle joining/starting a huddle
  const handleJoinHuddle = useCallback(async () => {
    if (!currentChannel) return;
    await joinHuddle(currentChannel.id);
  }, [currentChannel, joinHuddle]);

  // Handle leaving a huddle
  const handleLeaveHuddle = useCallback(async () => {
    if (!currentChannel) return;
    await leaveHuddle(currentChannel.id);
    setHuddleStatus(null);
  }, [currentChannel, leaveHuddle]);

  // Handle incoming call expiration
  const handleCallExpire = useCallback(() => {
    // Auto-decline expired calls
    if (pendingInvitations.length > 0) {
      declineCall(pendingInvitations[0].invitation_id);
    }
  }, [pendingInvitations, declineCall]);

  // Keyboard shortcut for search (Ctrl+F when chat is focused)
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if chat panel might be in focus (not in a form input elsewhere)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && currentChannel) {
        // Check if we're likely in the chat panel
        const activeElement = document.activeElement;
        const isInChat = activeElement?.closest('.chat-panel-container');
        if (isInChat) {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
      // Escape to close search
      if (e.key === 'Escape' && searchOpen) {
        clearSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentChannel, searchOpen, setSearchOpen, clearSearch]);

  return (
    <div className="chat-panel-container fixed bottom-28 right-5 w-[480px] md:w-[540px] h-[520px] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden z-[999997] border border-gray-200">
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-3">
          {/* Logo/Title */}
          <div className="flex items-center space-x-2">
            {currentChannel?.is_dm ? (
              /* DM icon */
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ) : (
              /* Channel/default icon */
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
            <span className="font-semibold">
              {currentChannel ? (
                currentChannel.is_dm
                  ? currentChannel.dm_other_participant?.display_name || 'Direct Message'
                  : currentChannel.display_name
              ) : 'NACME Connect'}
            </span>
          </div>

          {/* Connection indicator */}
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`}
               title={connectionState}></div>
        </div>

        {/* Window controls */}
        <div className="flex items-center space-x-1">
          {/* Channel settings button - only show for non-DM channels */}
          {currentChannel && !currentChannel.is_dm && (
            <button
              onClick={() => setShowChannelSettings(true)}
              className="p-1.5 hover:bg-blue-700 rounded transition-colors"
              title="Channel settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}

          {/* Pinned messages button - only show when channel is selected */}
          {currentChannel && !currentChannel.is_dm && (
            <button
              onClick={handleShowPinned}
              className={`p-1.5 hover:bg-blue-700 rounded transition-colors ${showPinnedMessages ? 'bg-blue-700' : ''}`}
              title="Pinned messages"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </button>
          )}

          {/* Search button - only show when channel is selected */}
          {currentChannel && (
            <button
              onClick={() => {
                if (searchOpen) {
                  clearSearch();
                } else {
                  setSearchOpen(true);
                }
              }}
              className={`p-1.5 hover:bg-blue-700 rounded transition-colors ${searchOpen ? 'bg-blue-700' : ''}`}
              title="Search messages"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          )}

          {/* Video call button - only show for DM conversations when video is enabled */}
          {videoEnabled && currentChannel?.is_dm && !isInCall && (
            <button
              onClick={handleStartDMCall}
              disabled={videoLoading}
              className={`p-1.5 hover:bg-blue-700 rounded transition-colors ${videoLoading ? 'opacity-50' : ''}`}
              title="Start video call"
            >
              {videoLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          )}

          {/* Huddle button - only show for non-DM channels when video is enabled */}
          {videoEnabled && currentChannel && !currentChannel.is_dm && !isInCall && (
            <StartHuddleButton
              onStart={handleJoinHuddle}
              isLoading={videoLoading}
            />
          )}

          {/* Settings button */}
          <div className="relative">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-1.5 hover:bg-blue-700 rounded transition-colors ${showSettings ? 'bg-blue-700' : ''}`}
              title="Notification Settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {/* Indicator dot for notification status */}
              {notificationPermission === 'granted' && notificationsEnabled && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full"></span>
              )}
            </button>

            {/* Settings dropdown */}
            {showSettings && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-3 py-2 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-800">Notification Settings</h4>
                </div>

                {/* Permission status */}
                {notificationPermission !== 'granted' && (
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">
                      {notificationPermission === 'denied'
                        ? 'Notifications are blocked. Enable them in your browser settings.'
                        : 'Enable browser notifications to stay updated.'}
                    </p>
                    {notificationPermission !== 'denied' && (
                      <button
                        onClick={requestNotificationPermission}
                        className="w-full px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
                      >
                        Enable Notifications
                      </button>
                    )}
                  </div>
                )}

                {/* Presence Status */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <h5 className="text-xs font-medium text-gray-500 mb-2">Your Status</h5>
                  <div className="space-y-1">
                    {[
                      { status: 'online', label: 'Online' },
                      { status: 'away', label: 'Away' },
                      { status: 'dnd', label: 'Do Not Disturb' },
                      { status: 'offline', label: 'Appear Offline' }
                    ].map(({ status, label }) => (
                      <button
                        key={status}
                        onClick={() => setPresenceStatus(status)}
                        className={`w-full flex items-center space-x-2 px-2 py-1.5 rounded text-xs transition-colors ${
                          myPresenceStatus === status
                            ? 'bg-blue-100 text-blue-700'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <PresenceIndicator status={status} size="sm" showBorder={false} />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Toggle options */}
                <div className="px-3 py-2 space-y-3">
                  {/* Notifications toggle */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-gray-700">Push notifications</span>
                    <button
                      onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        notificationsEnabled ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          notificationsEnabled ? 'translate-x-5' : ''
                        }`}
                      />
                    </button>
                  </label>

                  {/* Sound toggle */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-gray-700">Notification sound</span>
                    <button
                      onClick={() => setSoundEnabled(!soundEnabled)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        soundEnabled ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          soundEnabled ? 'translate-x-5' : ''
                        }`}
                      />
                    </button>
                  </label>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={minimizeChat}
            className="p-1.5 hover:bg-blue-700 rounded transition-colors"
            title="Minimize"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
            </svg>
          </button>
          <button
            onClick={closeChat}
            className="p-1.5 hover:bg-blue-700 rounded transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Click outside to close settings */}
      {showSettings && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Huddle indicator - shows when a channel has an active huddle */}
      {currentChannel && !currentChannel.is_dm && huddleStatus?.active && !isInCall && (
        <HuddleIndicator
          huddle={huddleStatus}
          onJoin={handleJoinHuddle}
          isLoading={videoLoading}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Channel List - always visible, fixed width */}
        <div className="w-[180px] flex-shrink-0 flex flex-col border-r border-gray-200 bg-gray-50 overflow-hidden">
          <ChannelList onChannelSelect={handleChannelSelect} />
        </div>

        {/* Message Area - takes remaining space */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          {currentChannel ? (
            <>
              {/* Search panel */}
              <ChatSearch />

              {/* Messages */}
              <div className="flex-1 overflow-hidden">
                <MessageList onSwitchToChannel={handleChannelSelect} />
              </div>

              {/* Typing indicator */}
              <TypingIndicator />

              {/* Input */}
              <div className="flex-shrink-0 border-t border-gray-200">
                <MessageInput />
              </div>
            </>
          ) : (
            /* No channel selected */
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center p-4">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm">Select a channel to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-500 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
        <span className="flex items-center space-x-1">
          <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
          <span>{isConnected ? 'Connected' : connectionState}</span>
          {/* Admin mode indicator */}
          {isAdmin && (
            <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium flex items-center space-x-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>Mod Mode</span>
            </span>
          )}
        </span>
        <span className="flex items-center space-x-1.5">
          <PresenceIndicator status={myPresenceStatus} size="sm" showBorder={false} />
          <span className="capitalize">{myPresenceStatus === 'dnd' ? 'Do Not Disturb' : myPresenceStatus}</span>
        </span>
      </div>

      {/* Pinned Messages Modal */}
      {showPinnedMessages && (
        <>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setShowPinnedMessages(false)}
          />
          {/* Modal */}
          <div className="absolute inset-4 bg-white rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-amber-50">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <h3 className="font-semibold text-gray-800">Pinned Messages</h3>
                <span className="text-xs text-gray-500">
                  ({pinnedMessages.length} {pinnedMessages.length === 1 ? 'message' : 'messages'})
                </span>
              </div>
              <button
                onClick={() => setShowPinnedMessages(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingPinned ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                </div>
              ) : pinnedMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  <p className="text-sm">No pinned messages yet</p>
                  <p className="text-xs mt-1">Pin important messages to find them easily</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pinnedMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-amber-300 transition-colors"
                    >
                      {/* Sender and time */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-800">
                            {msg.sender?.display_name || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {msg.created_at ? new Date(msg.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-amber-600">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                          </svg>
                          <span>
                            Pinned by {msg.pinned_by?.display_name || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      {/* Content */}
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                      {/* Thread replies indicator */}
                      {msg.thread_reply_count > 0 && (
                        <div className="mt-2 text-xs text-blue-600">
                          {msg.thread_reply_count} {msg.thread_reply_count === 1 ? 'reply' : 'replies'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Channel Settings Modal */}
      <ChannelSettingsModal
        isOpen={showChannelSettings}
        onClose={() => setShowChannelSettings(false)}
        channel={currentChannel}
      />

      {/* Video Room Overlay - shows when in a call */}
      {isInCall && videoToken && currentRoom && (
        <div className="fixed inset-0 z-[999999] bg-gray-900">
          <VideoRoom
            token={videoToken}
            roomId={currentRoom.hmsRoomId}
            roomType={currentRoom.type}
            calleeName={currentChannel?.dm_other_participant?.display_name}
            onLeave={() => {
              if (currentRoom.type === 'channel_huddle') {
                handleLeaveHuddle();
              } else {
                leaveCall();
              }
            }}
            onEnd={endCall}
          />
        </div>
      )}

      {/* Incoming Call Modal - shows when someone is calling */}
      {pendingInvitations.length > 0 && !isInCall && (
        <IncomingCallModal
          invitation={pendingInvitations[0]}
          onAccept={acceptCall}
          onDecline={declineCall}
          onExpire={handleCallExpire}
        />
      )}

      {/* Video error notification */}
      {videoError && (
        <div className="fixed bottom-32 right-8 z-[999998] bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 animate-slide-up">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{videoError}</span>
          <button
            onClick={clearVideoError}
            className="p-1 hover:bg-red-500 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
